import Vue from 'vue'
import Vuex from 'vuex'
import router from "@/router";
import categoryModule from "./category"
import productModule from "./product"
import fileModule from "./file"
import specificationModule from "./specification"
import orderModule from "./order"
import adminModule from "./admin"
import customerModule from "./customer"
import postModule from "./post"
import messageModule from "./message"
import contactUsModule from "./setting/contact_us"
import aboutUsModule from "./setting/about_us"
import footerModule from "./setting/footer"
import homePageModule from "./setting/home_page"
import seoModule from "./setting/seo"

Vue.use(Vuex)

const SHOW_ENGLISH_FIELDS_KEY = 'showEnglishFields'

function readShowEnglishFields() {
    try {
        return localStorage.getItem(SHOW_ENGLISH_FIELDS_KEY) === '1'
    } catch (e) {
        return false
    }
}


export default new Vuex.Store({
    state: {
        token: null,
        admin: null,
        unreadMessages: null,
        auth: null,
        loading: false,

        snackbarStatus: false,
        snackbarText: '',
        snackbarColor: '#67B930',

        windowIsSmallerThanMobile: false,
        windowIsSmallerThanTablet: false,
        windowIsSmallerThanLargeTablet: false,

        // English inputs are optional (the backend copies the Persian value
        // into empty ones) and hidden unless the admin asks for them.
        showEnglishFields: readShowEnglishFields(),
    },
    getters: {
        getAdmin(state) {
            return state.admin
        }
    },
    mutations: {
        setShowEnglishFields(state, value) {
            state.showEnglishFields = value
            try {
                localStorage.setItem(SHOW_ENGLISH_FIELDS_KEY, value ? '1' : '0')
            } catch (e) {
                // Private mode or blocked storage: the choice just won't persist.
            }
        },
        setToken(state, token) {
            state.token = token;
        },
        setAdmin(state, admin) {
            state.admin = admin;
        },
        setUnreadMessages(state, value) {
            state.unreadMessages = value;
        },
        setAuth(state, status) {
            state.auth = status
        },
        setLoading(state, condition) {
            state.loading = condition
        },
        setSnackbarStatus(state, value) {
            state.snackbarStatus = value
        },
        setSnackbarText(state, value) {
            state.snackbarText = value
        },
        setSnackbarColor(state, value) {
            state.snackbarColor = value
        },
        setWindowIsSmallerThanMobile(state, value) {
            state.windowIsSmallerThanMobile = value
        },
        setWindowIsSmallerThanTablet(state, value) {
            state.windowIsSmallerThanTablet = value
        },
        setWindowIsSmallerThanLargeTablet(state, value) {
            state.windowIsSmallerThanLargeTablet = value
        },
    },
    actions: {
        loginAdmin({commit}, authData) {
            return new Promise((resolve, reject) => {
                window.axios.post("/admin/login", {
                    email: authData.email,
                    password: authData.password,
                }).then((res) => {
                    Vue.$cookies.set('token', 'Bearer ' + res.data.data.token, 60 * 60 * 24 * 30)

                    commit('setAuth', true);
                    commit('setAdmin', res.data.data.admin);

                    resolve(res)
                }).catch((err) => {
                    commit('setAdmin', null);
                    commit('setAuth', false);

                    reject(err)
                })
            })
        },
        signOut({commit}) {
            window.axios.post("/admin/logout")
                .then(() => {
                    Vue.$cookies.remove('token')
                    commit('setAuth', false);
                    commit('setAdmin', null);
                    router.push('/login')
                })
                .catch(error => console.log(error.response))
        },
        getProfile({commit}) {
            return new Promise((resolve, reject) => {
                window.axios.get("/admin/detail")
                    .then((res) => {
                        commit('setAdmin', res.data.data.admin);
                        commit('setUnreadMessages', res.data.data.unread_messages);
                        commit('setAuth', true)
                        resolve(res)
                    })
                    .catch((err) => {
                        reject(err)
                    })
            })
        },
        updateProfile(_, data) {
            return new window.axios.post("/admin/update-profile", {
                name: data.name,
                email: data.email,
                old_password: data.old_password,
                new_password: data.new_password,
                new_password_confirmation: data.new_password_confirmation,
            }).then(() => {
            })
        },

        getDashboardData() {
            return new Promise((resolve, reject) => {
                window.axios.get("/admin/setting/dashboard")
                    .then((res) => {
                        resolve(res.data)
                    })
                    .catch((err) => {
                        reject(err)
                    })
            })
        },

        openSnackbar(context, {text, color = '#67B930'}) {
            context.commit("setSnackbarStatus", true)
            context.commit("setSnackbarColor", color)
            context.commit("setSnackbarText", text)
        },

        closeSnackbar(context) {
            context.commit("setSnackbarStatus", false)
            context.commit("setSnackbarColor", '#67B930')
            context.commit("setSnackbarText", null)
        },

        showValidationErrors(context, errors) {
            let errorText = ''
            Object.entries(errors).forEach((err) => {
                err[1].forEach((item) => {
                    errorText = errorText + item + '<br>'
                })
            })
            context.dispatch('openSnackbar', {text: errorText, color: 'error'}).then()
        },

        showResponseErrors(context, errRes) {
            if (errRes.response.status === 422) {
                context.dispatch("showValidationErrors", errRes.response.data.errors).then()
            } else {
                if (errRes.response && errRes.response.data && errRes.response.data.message) {
                    context.dispatch("openSnackbar", {text: errRes.response.data.message, color: 'error'}).then()
                } else if (errRes.message) {
                    context.dispatch("openSnackbar", {text: errRes.message, color: 'error'}).then()
                } else {
                    context.dispatch("openSnackbar", {text: 'Unknown Error!', color: 'error'}).then()
                }
            }
        },

        showSuccessResponse(context, res) {
            if (res && res.status === 200 && res.data.message) {
                context.dispatch("openSnackbar", {text: res.data.message}).then()
            }
        },

        objectParametersToString(_, params) {
            let stringedParams = '';
            Object.entries(params).forEach((param) => {
                if (param[1]) {
                    if (Array.isArray(param[1]) && param[1].length) {
                        param[1].forEach((item) => {
                            stringedParams = stringedParams + '&' + param[0] + '[]' + '=' + item
                        })
                    } else {
                        stringedParams = stringedParams + '&' + param[0] + '=' + param[1]
                    }
                }
            })

            return stringedParams;
        },
    },

    modules: {
        categoryModule,
        productModule,
        fileModule,
        specificationModule,
        orderModule,
        adminModule,
        customerModule,
        postModule,
        messageModule,
        contactUsModule,
        aboutUsModule,
        footerModule,
        homePageModule,
        seoModule,
    }
})